import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, CheckCircle, MagnifyingGlass, Phone, SpinnerGap, Star, Wrench, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Header, MobileNav } from "@/components/Header";
import StatusBadge from "@/components/StatusBadge";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { STATUSES, categoryByKey, formatDate, formatTime } from "@/lib/constants";

export default function ReportTrack() {
  const { ref } = useParams();
  const [searchParams] = useSearchParams();
  const providerToken = searchParams.get("prestataire") || "";
  const [report, setReport] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [confirmResolu, setConfirmResolu] = useState(false);
  const [rated, setRated] = useState(0);
  const [ratingBusy, setRatingBusy] = useState(false);
  const [review, setReview] = useState("");
  const [reviewSent, setReviewSent] = useState(false);

  const rateProvider = async (n) => {
    setRatingBusy(true);
    try {
      await api.post(`/services/${report.provider_id}/rate`, { rating: n });
      setRated(n);
      toast.success("Merci pour votre note !");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setRatingBusy(false);
  };

  const [reply, setReply] = useState("");
  const [replyBusy, setReplyBusy] = useState(false);
  const sendReply = async () => {
    setReplyBusy(true);
    try {
      await api.post(`/reports/${ref}/provider-message`, { token: providerToken, message: reply.trim() });
      toast.success("Message envoyé au gestionnaire");
      setReply("");
      const { data } = await api.get(`/reports/${ref}${providerToken ? `?prestataire=${encodeURIComponent(providerToken)}` : ""}`);
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setReplyBusy(false);
  };

  const sendReview = async () => {
    setRatingBusy(true);
    try {
      await api.post(`/services/${report.provider_id}/rate`, { rating: rated, comment: review.trim() });
      setReviewSent(true);
      toast.success("Merci ! Votre avis sera publié après validation.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setRatingBusy(false);
  };

  useEffect(() => {
    const q = providerToken ? `?prestataire=${encodeURIComponent(providerToken)}` : "";
    api.get(`/reports/${ref}${q}`).then((r) => setReport(r.data)).catch(() => setReport(null));
  }, [ref, providerToken]);

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setPhoto(data.path);
      setConfirmResolu(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setUploading(false);
  };

  const providerStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/reports/${ref}/provider-status`, {
        status,
        token: providerToken,
        photo: status === "resolu" && photo ? photo : null,
      });
      setReport(data);
      setPhoto("");
      toast.success(status === "resolu" ? "Signalement marqué comme résolu" : "Signalement marqué en cours");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  if (report === null) {
    return (
      <div className="min-h-screen pb-24 md:pb-0" data-testid="track-not-found">
        <Header />
        <main className="mx-auto max-w-lg px-5 py-20 text-center">
          <MagnifyingGlass size={48} weight="duotone" className="mx-auto text-muted-foreground" />
          <h1 className="mt-4 font-display text-3xl font-extrabold">Signalement introuvable</h1>
          <p className="mt-2 text-sm text-muted-foreground">Vérifiez la référence saisie (ex. SIG-K7M2P9).</p>
          <Link to="/signalements" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground">
            Voir tous les signalements
          </Link>
        </main>
        <MobileNav />
      </div>
    );
  }

  if (report === undefined) {
    return (
      <div className="min-h-screen" data-testid="track-loading">
        <Header />
        <main className="mx-auto max-w-2xl px-5 py-14">
          <div className="h-40 animate-pulse rounded-3xl bg-black/5" />
        </main>
      </div>
    );
  }

  const { Icon, label } = categoryByKey(report.category);
  const history = [...(report.status_history || [])].reverse();

  return (
    <div className="min-h-screen pb-24 md:pb-0" data-testid="track-page">
      <Header />
      <main className="mx-auto max-w-2xl px-5 py-10 sm:py-14">
        <Link to="/signalements" data-testid="track-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground transition-colors hover:text-primary">
          <ArrowLeft size={16} weight="bold" /> Tous les signalements
        </Link>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-3xl font-bold tracking-wider" data-testid="track-ref">{report.ref}</h1>
            <StatusBadge status={report.status} className="px-4 py-1.5 text-sm" />
            {report.urgent && (
              <span className="inline-flex items-center rounded-full bg-[#C8102E]/10 px-3 py-1.5 text-xs font-bold text-[#C8102E]" data-testid="track-urgent">
                Urgent
              </span>
            )}
            {report.issue && (
              <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary" data-testid="track-issue">
                {report.issue}
              </span>
            )}
          </div>

          <div className="mt-6 rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.15em] text-primary" data-testid="track-date">
              {formatDate(report.created_at)}
            </p>
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Icon size={22} weight="duotone" />
              </span>
              <div>
                <p className="font-display text-base font-bold">{label}</p>
                <p className="text-xs text-muted-foreground">
                  {report.location}
                  {report.sub_building ? ` · ${report.sub_building}` : ""}
                  {report.location_detail ? ` — ${report.location_detail}` : ""}
                </p>
                {report.provider_name && (
                  <p className="mt-1 text-xs font-bold text-primary" data-testid="track-provider">
                    {report.provider_suggested ? "Prestataire suggéré" : "Prestataire choisi"} : {report.provider_name}
                  </p>
                )}
              </div>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-foreground/80" data-testid="track-description">{report.description}</p>
            {report.photos?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-3" data-testid="track-photos">
                {report.photos.map((p, i) => (
                  <a key={p} href={fileUrl(p)} target="_blank" rel="noreferrer" data-testid={`track-photo-${i}`}>
                    <img src={fileUrl(p)} alt={`Photo ${i + 1}`} className="h-24 w-24 rounded-2xl border border-black/5 object-cover transition-transform duration-200 hover:scale-105" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {report.status === "resolu" && report.provider_id && report.notify_resolved !== false && !providerToken && (
            <div className="mt-6 rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="rate-provider-card">
              <p className="text-sm font-bold">
                {rated ? "Merci pour votre note !" : <>Comment s'est passée l'intervention de <span className="text-primary">{report.provider_name}</span> ?</>}
              </p>
              <div className="mt-3 flex items-center gap-1.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={ratingBusy}
                    onClick={() => rateProvider(n)}
                    data-testid={`rate-provider-star-${n}`}
                    aria-label={`Noter ${n} sur 5`}
                    className="transition-transform duration-150 hover:scale-110 disabled:opacity-50"
                  >
                    <Star size={28} weight={n <= rated ? "fill" : "regular"} className={n <= rated ? "text-[#E8A33D]" : "text-black/25"} />
                  </button>
                ))}
              </div>
              {!rated && <p className="mt-2 text-xs text-muted-foreground">Votre avis aide les autres résidents à choisir.</p>}
              {rated > 0 && !reviewSent && (
                <div className="mt-4">
                  <textarea
                    value={review}
                    onChange={(e) => setReview(e.target.value)}
                    rows={2}
                    maxLength={500}
                    placeholder="Ajoutez un petit commentaire (optionnel) — il sera publié après validation par le gestionnaire"
                    data-testid="rate-provider-comment"
                    className="w-full resize-none rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm leading-relaxed outline-none transition-colors focus:border-primary"
                  />
                  <button
                    type="button"
                    onClick={sendReview}
                    disabled={!review.trim() || ratingBusy}
                    data-testid="rate-provider-comment-submit"
                    className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-40"
                  >
                    {ratingBusy && <SpinnerGap size={14} className="animate-spin" />}
                    Envoyer mon avis
                  </button>
                </div>
              )}
              {reviewSent && (
                <p className="mt-3 text-xs text-muted-foreground" data-testid="rate-provider-comment-sent">
                  Votre avis sera publié après validation par le gestionnaire.
                </p>
              )}
            </div>
          )}

          {report.provider_access && (
            <div className="mt-6 rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="provider-section">
              <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-primary">
                <Wrench size={16} weight="duotone" /> Espace prestataire
              </p>
              <p className="mt-3 text-sm text-foreground/80" data-testid="provider-contact">
                Résident : <strong>{report.contact_name || "coordonnées masquées"}</strong>
                {report.contact_phone && (
                  <>
                    {" · "}
                    <a href={`tel:${report.contact_phone}`} data-testid="provider-phone" className="inline-flex items-center gap-1 font-bold text-primary">
                      <Phone size={13} weight="bold" /> {report.contact_phone}
                    </a>
                  </>
                )}
              </p>
              {report.status !== "resolu" && (
                <>
                  <div className="mt-4 flex items-center gap-3">
                    {photo ? (
                      <span className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-black/5">
                        <img src={fileUrl(photo)} alt="Photo de résolution" className="h-full w-full object-cover" data-testid="provider-photo-preview" />
                        <button
                          type="button"
                          onClick={() => setPhoto("")}
                          aria-label="Retirer la photo"
                          data-testid="provider-photo-remove"
                          className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white"
                        >
                          <X size={10} weight="bold" />
                        </button>
                      </span>
                    ) : (
                      <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-dashed border-black/15 px-4 py-2.5 text-xs font-bold text-muted-foreground transition-colors hover:border-primary hover:text-primary" data-testid="provider-photo-button">
                        {uploading ? <SpinnerGap size={14} className="animate-spin" /> : <Camera size={14} weight="duotone" />}
                        Photo de l'intervention (optionnel)
                        <input type="file" accept="image/*" hidden onChange={pickPhoto} data-testid="provider-photo-input" />
                      </label>
                    )}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {report.status === "nouveau" && (
                      <button
                        onClick={() => providerStatus("en_cours")}
                        disabled={busy || uploading}
                        data-testid="provider-status-encours"
                        className="inline-flex items-center gap-1.5 rounded-full border border-black/10 px-5 py-2.5 text-xs font-bold transition-colors hover:border-primary hover:text-primary disabled:opacity-60"
                      >
                        {busy && <SpinnerGap size={13} className="animate-spin" />}
                        Marquer en cours
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (!photo && !confirmResolu) {
                          setConfirmResolu(true);
                          return;
                        }
                        setConfirmResolu(false);
                        providerStatus("resolu");
                      }}
                      disabled={busy || uploading}
                      data-testid="provider-status-resolu"
                      className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-xs font-bold transition-colors disabled:opacity-60 ${
                        confirmResolu && !photo
                          ? "bg-[#E8A33D] text-white hover:bg-[#C98A2B]"
                          : "bg-[#007A4D] text-white hover:bg-[#00925E]"
                      }`}
                    >
                      {busy ? <SpinnerGap size={13} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
                      {confirmResolu && !photo ? "Confirmer sans photo" : "Marquer résolu"}
                    </button>
                  </div>
                  {confirmResolu && !photo && (
                    <p className="mt-2 text-xs font-semibold text-[#9C6A1B]" data-testid="provider-photo-nudge">
                      Une photo de l'intervention rassure les résidents — ajoutez-la ci-dessus, ou confirmez sans photo.
                    </p>
                  )}
                  <div className="mt-5 border-t border-black/5 pt-4">
                    <label htmlFor="provider-reply" className="text-xs font-bold">Répondre au gestionnaire</label>
                    <textarea
                      id="provider-reply"
                      rows={2}
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="Votre message (disponibilité, question, précision…)"
                      data-testid="provider-reply-input"
                      className="mt-2 w-full resize-none rounded-2xl border border-black/10 bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-primary"
                    />
                    <button
                      type="button"
                      onClick={sendReply}
                      disabled={reply.trim().length < 2 || replyBusy}
                      data-testid="provider-reply-send"
                      className="mt-2 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-40"
                    >
                      {replyBusy && <SpinnerGap size={13} className="animate-spin" />}
                      Envoyer au gestionnaire
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <h2 className="mt-10 font-display text-xl font-bold tracking-tight">Suivi du dossier</h2>
          <div className="mt-5 space-y-0" data-testid="track-timeline">
            {history.map((h, i) => {
              const s = STATUSES[h.status] || STATUSES.nouveau;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="relative flex gap-4 pb-8"
                  data-testid={`timeline-entry-${i}`}
                >
                  {i < history.length - 1 && <span className="absolute left-[9px] top-6 h-full w-px bg-black/10" />}
                  <span className="relative z-10 mt-1 h-5 w-5 shrink-0 rounded-full border-4 border-background" style={{ backgroundColor: s.text }} />
                  <div>
                    <p className="text-sm font-bold" style={{ color: s.text }}>{h.message ? "Message du prestataire" : s.label}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(h.at)} · {formatTime(h.at)}
                    </p>
                    {h.comment && <p className="mt-1.5 text-sm italic text-foreground/70">« {h.comment} »</p>}
                    {h.message && (
                      <p className="mt-1.5 rounded-2xl bg-primary/5 px-3 py-2 text-sm text-foreground/80" data-testid={`timeline-message-${i}`}>« {h.message} »</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </main>
      <MobileNav />
    </div>
  );
}
